/**
 * Admin component for managing Songs Analyzer station assignments
 * Features: Assign stations, set duration, suspend/unsuspend, delete access
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Users, Radio, Check, X, Search, Save, Plus, RefreshCw, 
  Edit2, Trash2, PauseCircle, PlayCircle, Calendar, Clock,
  AlertTriangle, Info
} from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const AdminSongsAnalyzerAccess = () => {
  const backendUrl = getBackendUrl();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [availableStations, setAvailableStations] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedStations, setSelectedStations] = useState([]);
  const [proAnalyticsStations, setProAnalyticsStations] = useState([]);
  const [listenerStatsStations, setListenerStatsStations] = useState([]); // NEW: Pro Analytics access
  // v2.9.192 — per-prefecture access for the "Νομός" report tab.
  const [prefectureAccess, setPrefectureAccess] = useState([]);
  const [availablePrefectures, setAvailablePrefectures] = useState([]);
  const [accessExpires, setAccessExpires] = useState('');
  const [userNotes, setUserNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${backendUrl}/api/songs-analyzer/admin/users-with-access`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setUsers(data.users || []);
      setAvailableStations(data.available_stations || []);

      // v2.9.192 — also load the list of prefectures available for assignment
      try {
        const prefRes = await fetch(`${backendUrl}/api/songs-analyzer/admin/available-prefectures`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (prefRes.ok) {
          const prefData = await prefRes.json();
          setAvailablePrefectures(prefData.prefectures || []);
        }
      } catch (err) {
        // non-blocking — UI just won't render the Νομός section
      }
      
      try {
        const allUsersRes = await fetch(`${backendUrl}/api/admin/users-with-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (allUsersRes.ok) {
          const allUsersData = await allUsersRes.json();
          const allUsersList = allUsersData.users || allUsersData || [];
          setAllUsers(allUsersList);
        }
      } catch (err) {
        setAllUsers(data.users || []);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Σφάλμα φόρτωσης');
      setLoading(false);
    }
  };

  const handleEdit = async (user) => {
    const userId = user.user_id || user.id;
    
    // Fetch detailed access info
    try {
      const response = await fetch(`${backendUrl}/api/songs-analyzer/admin/user-access-details/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const details = await response.json();
        setEditingUser({...user, ...details});
        setSelectedStations(details.assigned_stations || []);
        setProAnalyticsStations(details.pro_analytics_stations || []);
        setListenerStatsStations(details.listener_stats_stations || []);
        setPrefectureAccess(details.prefecture_access || user.prefecture_access || []);
        setAccessExpires(details.access_expires ? details.access_expires.split('T')[0] : '');
        setUserNotes(details.notes || '');
      } else {
        setEditingUser(user);
        setSelectedStations(user.assigned_stations || []);
        setProAnalyticsStations(user.pro_analytics_stations || []);
        setListenerStatsStations(user.listener_stats_stations || []);
        setPrefectureAccess(user.prefecture_access || []);
        setAccessExpires('');
        setUserNotes('');
      }
    } catch (err) {
      setEditingUser(user);
      setSelectedStations(user.assigned_stations || []);
      setProAnalyticsStations([]);
      setListenerStatsStations([]);
      setPrefectureAccess([]);
      setAccessExpires('');
      setUserNotes('');
    }
  };

  // v2.9.192 — toggle a single prefecture in/out of the user's allow list.
  const handlePrefectureToggle = (prefectureKey) => {
    setPrefectureAccess(prev =>
      prev.includes(prefectureKey)
        ? prev.filter(k => k !== prefectureKey)
        : [...prev, prefectureKey]
    );
  };

  const handleProAnalyticsToggle = (stationId) => {
    setProAnalyticsStations(prev => 
      prev.includes(stationId)
        ? prev.filter(id => id !== stationId)
        : [...prev, stationId]
    );
  };

  const handleListenerStatsToggle = (stationId) => {
    setListenerStatsStations(prev => 
      prev.includes(stationId)
        ? prev.filter(id => id !== stationId)
        : [...prev, stationId]
    );
  };

  const handleStationToggle = (stationId) => {
    setSelectedStations(prev => 
      prev.includes(stationId)
        ? prev.filter(id => id !== stationId)
        : [...prev, stationId]
    );
  };

  const handleSave = async () => {
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const userId = editingUser.user_id || editingUser.id;
      const response = await fetch(`${backendUrl}/api/songs-analyzer/admin/update-access`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_id: userId,
          station_ids: selectedStations,
          access_expires: accessExpires ? `${accessExpires}T23:59:59Z` : null,
          notes: userNotes || null,
          pro_analytics_access: proAnalyticsStations,
          listener_stats_access: listenerStatsStations,
          // v2.9.192 — per-prefecture access for the "Νομός" tab.
          prefecture_access: prefectureAccess
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Ενημερώθηκε η πρόσβαση για ${editingUser.username}`);
        fetchData();
        setEditingUser(null);
      } else {
        throw new Error(data.detail || 'Error saving');
      }
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης: ' + err.message);
    }
    setSaving(false);
  };

  const handleSuspend = async (user, suspend) => {
    const userId = user.user_id || user.id;
    setActionLoading(userId);
    
    try {
      const response = await fetch(`${backendUrl}/api/songs-analyzer/admin/suspend-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_id: userId,
          is_suspended: suspend,
          reason: suspend ? 'Αναστολή από διαχειριστή' : null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(suspend ? 'Η πρόσβαση ανεστάλη' : 'Η πρόσβαση επαναφέρθηκε');
        fetchData();
      } else {
        throw new Error(data.detail || 'Error');
      }
    } catch (err) {
      toast.error('Σφάλμα: ' + err.message);
    }
    setActionLoading(null);
  };

  const handleDelete = async (user) => {
    const userId = user.user_id || user.id;
    setActionLoading(userId);
    
    try {
      const response = await fetch(`${backendUrl}/api/songs-analyzer/admin/remove-access/${userId}?remove_stations=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Η πρόσβαση αφαιρέθηκε');
        fetchData();
      } else {
        throw new Error(data.detail || 'Error');
      }
    } catch (err) {
      toast.error('Σφάλμα: ' + err.message);
    }
    setActionLoading(null);
    setConfirmDelete(null);
  };

  const displayUsers = showAllUsers ? allUsers : users;
  const filteredUsers = displayUsers.filter(u => 
    !searchTerm || 
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Φόρτωση...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Radio className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Songs Analyzer Access</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Διαχείριση πρόσβασης χρηστών ({availableStations.length} σταθμοί)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAllUsers(!showAllUsers)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              showAllUsers 
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {showAllUsers ? 'Μόνο με Πακέτο' : 'Όλοι οι Χρήστες'}
          </button>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            title="Ανανέωση"
          >
            <RefreshCw className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 mb-4">
        <Search className="w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Αναζήτηση χρήστη..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-transparent border-none outline-none text-sm flex-1 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
      </div>

      {/* Available Stations Summary */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Διαθέσιμοι Σταθμοί</h3>
        <div className="flex flex-wrap gap-2">
          {availableStations.map(station => (
            <span 
              key={station.id}
              className="px-2 py-1 bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-200 text-xs rounded-full"
            >
              {station.name}
            </span>
          ))}
          {availableStations.length === 0 && (
            <span className="text-blue-600 dark:text-blue-400 text-sm">Δεν υπάρχουν διαθέσιμοι σταθμοί</span>
          )}
        </div>
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Δεν βρέθηκαν χρήστες</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map(user => {
            const userId = user.user_id || user.id;
            const assigned = user.assigned_stations || [];
            const stationNames = user.station_names || [];
            const isSuspended = user.songs_analyzer_suspended;
            const isLoading = actionLoading === userId;
            
            return (
              <div 
                key={userId}
                className={`border rounded-lg p-4 transition-colors ${
                  isSuspended 
                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/10' 
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-800 dark:text-white">
                        {user.username || 'Χωρίς όνομα'}
                      </h3>
                      {user.role === 'admin' && (
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded">
                          Admin
                        </span>
                      )}
                      {isSuspended && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <PauseCircle className="w-3 h-3" />
                          Σε Αναστολή
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || '-'}</p>
                    
                    {/* Stations */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {stationNames.length > 0 ? (
                        stationNames.map((name, idx) => (
                          <span 
                            key={idx}
                            className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full"
                          >
                            {name}
                          </span>
                        ))
                      ) : assigned.length > 0 ? (
                        assigned.map((id, idx) => {
                          const station = availableStations.find(s => s.id === id);
                          return (
                            <span 
                              key={idx}
                              className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full"
                            >
                              {station?.name || id.slice(0, 8) + '...'}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-sm text-orange-500 dark:text-orange-400 italic">
                          Χωρίς σταθμό
                        </span>
                      )}
                    </div>
                    
                    {/* Expiry info */}
                    {user.songs_analyzer_expires && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Λήγει: {new Date(user.songs_analyzer_expires).toLocaleDateString('el-GR')}
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Edit */}
                    <button
                      onClick={() => handleEdit(user)}
                      disabled={isLoading}
                      className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                      title="Επεξεργασία"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    
                    {/* Suspend/Unsuspend */}
                    <button
                      onClick={() => handleSuspend(user, !isSuspended)}
                      disabled={isLoading}
                      className={`p-2 rounded-lg transition-colors ${
                        isSuspended
                          ? 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                          : 'text-yellow-600 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                      }`}
                      title={isSuspended ? 'Επαναφορά' : 'Αναστολή'}
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      ) : isSuspended ? (
                        <PlayCircle className="w-4 h-4" />
                      ) : (
                        <PauseCircle className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Delete */}
                    <button
                      onClick={() => setConfirmDelete(user)}
                      disabled={isLoading}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Διαγραφή πρόσβασης"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Επεξεργασία: {editingUser.username}
              </h3>
              <button 
                onClick={() => setEditingUser(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            
            {/* Stations Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Σταθμοί
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableStations.map(station => (
                  <label
                    key={station.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedStations.includes(station.id)
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 dark:border-purple-400'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStations.includes(station.id)}
                      onChange={() => handleStationToggle(station.id)}
                      className="w-4 h-4 text-purple-500 rounded accent-purple-500"
                    />
                    <span className="flex-1 text-gray-900 dark:text-white font-medium">
                      {station.name}
                    </span>
                    {selectedStations.includes(station.id) && (
                      <Check className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                    )}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Επιλεγμένοι: {selectedStations.length} σταθμοί
                {selectedStations.length > 1 && (
                  <span className="text-orange-500 ml-2">
                    (+€{(selectedStations.length - 1) * 100}/μήνα)
                  </span>
                )}
              </p>
            </div>
            
            {/* Access Expiry */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Ημερομηνία Λήξης (προαιρετικό)
              </label>
              <input
                type="date"
                value={accessExpires}
                onChange={(e) => setAccessExpires(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Αφήστε κενό για απεριόριστη πρόσβαση
              </p>
            </div>
            
            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Info className="w-4 h-4 inline mr-1" />
                Σημειώσεις (προαιρετικό)
              </label>
              <textarea
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                placeholder="Π.χ. Ειδική τιμή, παράταση δοκιμαστικής..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-20 resize-none"
              />
            </div>
            
            {/* Pro Analytics Access */}
            {selectedStations.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📊 Pro Analytics Access
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Ενεργοποιήστε τη λειτουργία Pro Analytics για τους επιλεγμένους σταθμούς
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  {selectedStations.map(stationId => {
                    const station = availableStations.find(s => s.id === stationId);
                    return (
                      <label
                        key={stationId}
                        className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-colors ${
                          proAnalyticsStations.includes(stationId)
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-400'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={proAnalyticsStations.includes(stationId)}
                          onChange={() => handleProAnalyticsToggle(stationId)}
                          className="w-4 h-4 text-emerald-500 rounded accent-emerald-500"
                        />
                        <span className="flex-1 text-gray-900 dark:text-white text-sm">
                          {station?.name || stationId}
                        </span>
                        {proAnalyticsStations.includes(stationId) && (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                            ✓ Pro
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
                  {proAnalyticsStations.length > 0 
                    ? `📊 ${proAnalyticsStations.length} σταθμ${proAnalyticsStations.length === 1 ? 'ός' : 'οί'} με Pro Analytics`
                    : 'Καμία ενεργοποίηση Pro Analytics'
                  }
                </p>
              </div>
            )}
            
            {/* Listener Stats Access */}
            {selectedStations.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  👥 Στατιστικά Ακροατών
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Ενεργοποιήστε τα στατιστικά ακροατών για τους επιλεγμένους σταθμούς
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  {selectedStations.map(stationId => {
                    const station = availableStations.find(s => s.id === stationId);
                    return (
                      <label
                        key={stationId}
                        className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-colors ${
                          listenerStatsStations.includes(stationId)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={listenerStatsStations.includes(stationId)}
                          onChange={() => handleListenerStatsToggle(stationId)}
                          className="w-4 h-4 text-blue-500 rounded accent-blue-500"
                          data-testid={`listener-stats-toggle-${stationId}`}
                        />
                        <span className="flex-1 text-gray-900 dark:text-white text-sm">
                          {station?.name || stationId}
                        </span>
                        {listenerStatsStations.includes(stationId) && (
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            ✓ Listeners
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  {listenerStatsStations.length > 0 
                    ? `👥 ${listenerStatsStations.length} σταθμ${listenerStatsStations.length === 1 ? 'ός' : 'οί'} με Στατιστικά Ακροατών`
                    : 'Καμία ενεργοποίηση Στατιστικών Ακροατών'
                  }
                </p>
              </div>
            )}

            {/* v2.9.192 — Νομός (Prefecture) Access */}
            {availablePrefectures.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🗺️ Νομός Access
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Επιλέξτε τους νομούς στους οποίους ο χρήστης έχει πρόσβαση μέσω της καρτέλας &quot;Νομός&quot; του Songs Analyzer
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setPrefectureAccess(availablePrefectures.map(p => p.key))}
                    className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/60"
                    data-testid="prefecture-select-all-btn"
                  >
                    Επιλογή όλων
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrefectureAccess([])}
                    className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    data-testid="prefecture-clear-btn"
                  >
                    Καθαρισμός
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  {availablePrefectures.map(pref => (
                    <label
                      key={pref.key}
                      className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${
                        prefectureAccess.includes(pref.key)
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 dark:border-purple-400'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={prefectureAccess.includes(pref.key)}
                        onChange={() => handlePrefectureToggle(pref.key)}
                        className="w-4 h-4 text-purple-500 rounded accent-purple-500"
                        data-testid={`prefecture-toggle-${pref.key}`}
                      />
                      <span className="flex-1 text-gray-900 dark:text-white text-sm truncate">
                        {pref.label}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {pref.station_count}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-2" data-testid="prefecture-access-summary">
                  {prefectureAccess.length > 0
                    ? `🗺️ ${prefectureAccess.length} νομ${prefectureAccess.length === 1 ? 'ός' : 'οί'} με πρόσβαση`
                    : 'Καμία πρόσβαση Νομού'
                  }
                </p>
              </div>
            )}

            {/* Pricing Info */}
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 mb-6">
              <p className="text-sm text-orange-700 dark:text-orange-300">
                💰 Τιμολόγηση: 1ος σταθμός συμπεριλαμβάνεται στο πακέτο, +€100/μήνα για κάθε επιπλέον
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Αποθήκευση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Διαγραφή Πρόσβασης
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Είστε σίγουροι ότι θέλετε να αφαιρέσετε την πρόσβαση του χρήστη <strong>{confirmDelete.username}</strong> στο Songs Analyzer;
              <br /><br />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Αυτή η ενέργεια θα αφαιρέσει όλους τους ανατεθειμένους σταθμούς.
              </span>
            </p>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Ακύρωση
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Διαγραφή
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSongsAnalyzerAccess;
